import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper, {
  ScrollFadeSection,
} from '../../ScrollFadeWrapper/ScrollFadeWrapper';

const CARD_SHADOW =
  '0px 1px 2px 0px rgba(162, 172, 177, 0.2), 0px 12px 24px -4px rgba(22, 47, 59, 0.12)';

const badgeStyles = {
  position: 'absolute',
  top: '-16px',
  left: '16px',
  width: '70px',
  height: '70px',
  borderRadius: '20px',
  backgroundColor: '#C7CA00',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0px 6px 16px 0px #162F3B14',
};

const numberStyles = {
  fontFamily: 'Pally, Inter, sans-serif',
  fontWeight: 700,
  fontSize: '3rem',
  lineHeight: '1',
  letterSpacing: '-0.03em',
  color: '#162F3B',
};

const DEFAULT_VISIBLE_CARD_COUNT = 5;
const CARD_MIN_WIDTH = 196;
const CARD_GAP = 16;
const formatCardText = (text = '') => String(text).replace(/\r?\n/g, '<br />');

const KeyPointHorizontalNumericComponent = ({ data }) => {
  const { cards = [] } = data || {};
  const hasOverflowCards = cards.length > DEFAULT_VISIBLE_CARD_COUNT;
  const scrollTrackMinWidth = `${
    cards.length * CARD_MIN_WIDTH + Math.max(cards.length - 1, 0) * CARD_GAP
  }px`;
  const scrollRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const initialScrollLeftRef = useRef(0);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!isDraggingRef.current || !scrollRef.current) return;

      const deltaX = event.clientX - dragStartXRef.current;
      scrollRef.current.scrollLeft = initialScrollLeftRef.current - deltaX;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (event) => {
    if (!hasOverflowCards || !scrollRef.current) return;

    isDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    initialScrollLeftRef.current = scrollRef.current.scrollLeft;
    event.preventDefault();
  };

  return (
    <ScrollFadeSection
      component="section"
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1100px',
          pt: '25px',
          pb: '16px',
          boxSizing: 'border-box',
        }}
      >
        {cards.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '140px',
              right: '122px',
              backgroundColor: '#E3E6E8',
              borderRadius: '40px',
            }}
          />
        )}

        <Box
          sx={{
            position: 'relative',
          }}
        >
          <Box
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            sx={{
              overflowX: hasOverflowCards ? 'auto' : 'visible',
              overflowY: hasOverflowCards ? 'hidden' : 'visible',
              pt: hasOverflowCards ? '20px' : 0,
              px: hasOverflowCards ? '8px' : 0,
              pb: hasOverflowCards ? '8px' : 0,
              cursor: hasOverflowCards ? 'grab' : 'default',
              userSelect: hasOverflowCards ? 'none' : 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              '&:active': {
                cursor: hasOverflowCards ? 'grabbing' : 'default',
              },
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: hasOverflowCards
                  ? `repeat(${cards.length}, minmax(${CARD_MIN_WIDTH}px, 1fr))`
                  : `repeat(${DEFAULT_VISIBLE_CARD_COUNT}, minmax(0, 1fr))`,
                gap: `${CARD_GAP}px`,
                alignItems: 'stretch',
                minWidth: hasOverflowCards ? scrollTrackMinWidth : '100%',
              }}
            >
              {cards.map(({ number, text }, index) => (
                <ScrollFadeWrapper
                  key={`${number}-${index}`}
                  delay={index * 0.1}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      minHeight: '138px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '28px',
                      pt: '70px',
                      px: '24px',
                      pb: '24px',
                      boxSizing: 'border-box',
                      boxShadow: CARD_SHADOW,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={badgeStyles}>
                      <Typography sx={numberStyles}>{number}</Typography>
                    </Box>

                    <Box
                      component="div"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        lineHeight: '1.2rem',
                        letterSpacing: '-0.03em',
                        color: '#162F3B',
                        textAlign: 'left',
                        maxWidth: '145px',
                      }}
                      dangerouslySetInnerHTML={{ __html: formatCardText(text) }}
                    />
                  </Box>
                </ScrollFadeWrapper>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </ScrollFadeSection>
  );
};

export default KeyPointHorizontalNumericComponent;
